import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

/**
 * A dialog that prompts the user for a single text input before confirming an action.
 *
 * #### Usage
 *
 * ```ts
 * const dialogRef = this.dialog.open(InputDialog, {
 *   data: {
 *     title: 'users.disableUserTitle',
 *     description: 'users.disableCommentPrompt',
 *     placeholder: 'users.disableCommentPlaceholder',
 *     required: true,
 *   } as InputDialogData,
 * });
 * dialogRef.afterClosed().subscribe((comment: string | undefined) => {
 *   if (comment !== undefined) {
 *     // user confirmed; use comment
 *   }
 * });
 * ```
 */
@Component({
  selector: 'cipo-input-dialog',
  template: `
    <h2 mat-dialog-title data-testid="cipo-input-dialog-title">{{ data.title | translate }}</h2>
    <mat-dialog-content>
      <div class="p-t">
        @if (data.description) {
          <p [innerHTML]="data.description | translate"></p>
        }
        <mat-form-field class="cipo--width--full">
          <mat-label>{{ data.label ?? data.placeholder | translate }}</mat-label>
          <input
            matInput
            data-testid="cipo-input-dialog-input"
            [(ngModel)]="value"
            [errorStateMatcher]="errorStateMatcher"
            [placeholder]="data.placeholder ? (data.placeholder | translate) : ''"
            [required]="data.required ?? false"
          />
          @if (value() && !isValid()) {
            <mat-error data-testid="cipo-input-dialog-validation-error">
              {{ data.validationLabel ?? 'errors.incorrectValue' | translate }}
            </mat-error>
          }
        </mat-form-field>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-raised-button data-testid="cipo-input-dialog-cancel" [mat-dialog-close]="undefined">
        {{ data.noLabel ?? ('actions.cancel' | translate) }}
      </button>
      <button
        mat-raised-button
        data-testid="cipo-input-dialog-confirm"
        [disabled]="(data.required && !value().trim()) || !isValid()"
        [mat-dialog-close]="value()"
        color="accent"
        cdkFocusInitial
      >
        {{ data.yesLabel ?? ('actions.confirm' | translate) }}
      </button>
    </mat-dialog-actions>
  `,
  imports: [MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, FormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputDialog {
  readonly data = inject<InputDialogData>(MAT_DIALOG_DATA);
  readonly value = signal('');

  readonly isValid = computed(() => !this.data.validationValue || this.value().trim() === this.data.validationValue);

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => !!this.value() && !this.isValid(),
  };
}

export interface InputDialogData {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  /** When set, the confirm button is disabled until the entered value matches exactly. */
  validationValue?: string;
  /** Translation key shown as an error when the value does not match `validationValue`. */
  validationLabel?: string;
  yesLabel?: string;
  noLabel?: string;
}
